import {FC, useState, useEffect} from 'react'
import {Card, Row, Col, Spinner, Button, Form, InputGroup} from 'react-bootstrap'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {BoundTariffModel} from '../../tariff/core/_models'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import {
  createTariffByType,
  getTariffByTypeAndID,
  updateTariffByTypeAndID,
  getHSDetail,
} from '../core/_requests'
import {PageTitle} from '../../../../../_metronic/layout/core'
import EconDropdown from '../../../../../ui-kit/EconDropdown'
import HSDropdown from './HSDropdown'
import HSDropdownWithLoadMore from './HSDropdownWithLoadMore'
import HSDescriptionDetailView from './HSDescriptionDetailView'
import { Toast, showSwal } from '../../../../../ui-kit'

const confirmDialog = (title: string) =>
  showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })

const INITIAL_STATE: BoundTariffModel = {
  rep: '',
  hs: '',
  hs_version: '',
  ave_avg: 0,
}

export type BoundTariffFormType = {
  subject: string
  type: string
  HSVersions: any[]
  HSCodes: any[]
}

const BoundTariffForm: FC<BoundTariffFormType> = ({subject, type, HSCodes, HSVersions}) => {
  const ability = useAbility(AbilityContext)
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const {idTariff} = useParams()

  const isDetailMode = Boolean(idTariff)
  const isAddMode = !idTariff

  const [isEditMode, setIsEditMode] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [hsDescriptionData, setHSDescriptionData] = useState<any>(null)
  const [formData, setFormData] = useState<BoundTariffModel>({
    ...INITIAL_STATE,
  })

  const isTariffDataEditable = isAddMode || isEditMode

  const validateForm = () => {
    const requiredFields: (keyof BoundTariffModel)[] = ['rep', 'hs', 'hs_version', 'ave_avg']
    const isValid = requiredFields.every((key) => {
      const val = formData[key]
      return (
        val !== undefined && val !== null && String(val).trim() !== '0' && String(val).trim() !== ''
      )
    })
    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateForm()
  }, [formData])

  useEffect(() => {
    if (!isDetailMode) return
    const fetch = async () => {
      try {
        setIsLoadingData(true)
        const res = await getTariffByTypeAndID(axiosPrivate, type, idTariff)
        if (res?.data?.data) {
          const data = res.data.data
          setFormData(data)
          console.log(res)

          // Fetch detailed HS description
          try {
            const resDetail = await getHSDetail(axiosPrivate, data.hs, data.hs_version)
            if (resDetail?.data?.data) {
              setHSDescriptionData(resDetail.data.data)
            }
          } catch (err) {
            console.error('Failed to fetch detailed HS description', err)
            Toast(
              'Error when fetching tariff data',
              'danger',
              <Link to={`/master-data-hs-descriptions/add?ver=${data.hs_version}&code=${data.hs}`}>
                Create new HS Description
              </Link>
            )
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingData(false)
      }
    }
    fetch()
  }, [idTariff])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
  }

  const handleSubmit = () => {
    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this Bound Tariff?`
    ).then((result) => {
      if (!result.isConfirmed) return
      setIsLoadingButton(true)

      const action = isEditMode
        ? updateTariffByTypeAndID(axiosPrivate, type, formData)
        : createTariffByType(axiosPrivate, type, formData)

      const successStatus = isEditMode ? 200 : 201
      const successMessage = isEditMode ? 'Data updated' : 'Data created'
      const errorMessage = isEditMode
        ? 'Failed to update Bound Tariff'
        : 'Failed to create Bound Tariff'

      action
        .then((res) => {
          if (res.status === successStatus) {
            showSwal({title: successMessage, icon: 'success', confirmButtonText: 'OK'}).then(
              () => navigate(`/tariff/bound`)
            )
          }
        })
        .catch((err) => {
          console.error(err)
          const message = err?.response?.data?.message || errorMessage
          showSwal('Error', message, 'error')
        })
        .finally(() => setIsLoadingButton(false))
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (!result.isConfirmed) return
      if (isEditMode) {
        setIsEditMode(false)
      } else {
        navigate('/tariff/bound')
      }
    })
  }

  const ReadOnlyField = ({value}) => <div className='data-value'>{value ?? '-'}</div>

  const fieldLabel = (label: string, required = false) => (
    <Form.Label
      className={`text-primary me-0 ps-0 ${required && isTariffDataEditable ? 'required' : ''}`}
      column
    >
      {label}
    </Form.Label>
  )

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
          {
            title: 'Tariff',
            path: '/tariff',
            isSeparator: false,
            isActive: false,
          },
          {title: '', path: '', isSeparator: true, isActive: false},
          {
            title: 'Bound Tariff',
            path: '/tariff/bound',
            isSeparator: false,
            isActive: false,
          },
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {isDetailMode ? 'Detail' : isEditMode ? 'Edit' : 'Add'}
      </PageTitle>

      <div className='MasterDataPage'>
        <Row>
          <Col>
            <Card className='mb-5'>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode ? `Bound Tariff Detail` : 'Add Bound Tariff'}
                      </h3>
                    </div>
                    {!isEditMode && isDetailMode && ability.can('update', subject) && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>

              <Card.Body>
                {isLoadingData ? (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading<span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <Form>
                      <div className='row'>
                        {/* REP */}
                        <div className='col-md-6 mb-5'>
                          {fieldLabel('Economy', true)}
                          {isTariffDataEditable ? (
                            <EconDropdown
                              econ={formData.rep}
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  rep: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.rep} />
                          )}
                        </div>

                        {/* HS Version */}
                        <div className='col-md-6 mb-5'>
                          {fieldLabel('HS Version', true)}
                          {isTariffDataEditable ? (
                            <HSDropdown
                              items={HSVersions}
                              value={formData.hs_version}
                              onSelect={(hs) =>
                                setFormData({
                                  ...formData,
                                  hs_version: hs,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.hs_version} />
                          )}
                        </div>

                        {/* HS Code */}
                        <div className='col-md-6 mb-5'>
                          {fieldLabel('HS Code', true)}
                          {isTariffDataEditable ? (
                            <HSDropdownWithLoadMore
                              value={formData.hs}
                              onSelect={(hs) =>
                                setFormData({
                                  ...formData,
                                  hs: hs,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.hs} />
                          )}
                        </div>

                        {/* Ave Avg */}
                        <div className='col-md-6 mb-5'>
                          {fieldLabel('AVE Average', true)}
                          {isTariffDataEditable ? (
                            <InputGroup className='mb-3'>
                              <Form.Control
                                placeholder='Enter AVE average'
                                type='number'
                                step='0.01'
                                name='ave_avg'
                                value={formData.ave_avg}
                                onChange={handleChange}
                              />
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup>
                          ) : (
                            <ReadOnlyField value={formData.ave_avg} />
                          )}
                        </div>
                      </div>

                      {isTariffDataEditable && (
                        <div className='row'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isFormValid}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form>

                    {isDetailMode && (
                      <>
                        <hr className='my-10' />
                        <HSDescriptionDetailView hsDescriptionData={hsDescriptionData} />
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default BoundTariffForm
